import React, { useEffect, useRef, useState } from "react";
import { cn } from "@/libs/utils";
import useUser from "@/api/apis";
import { LoginDialog } from "@/v2-components/ui/_icons/LoginDialog";
import { useIsMobileView } from "@/contexts/ResponsiveProvider";
import { useTrans } from "@/utils/useTrans";

const SUPPORTED_IDS = new Set([
  "Aatrox",
  "Katarina",
  "Jhin",
  "Malphite",
  "Kaisa",
  "Graves",
  "Syndra",
  "Garen",
  "Orianna",
  "DrMundo",
  "Yasuo",
  "Tristana",
  "Khazix",
  "Sett",
  "Nami",
  "Caitlyn",
  "Milio",
  "Thresh",
  "Ashe",
  "Jinx",
  "JarvanIV",
  "Bard",
  "Sion",
  "Janna",
  "Darius",
  "Shaco",
  "Soraka",
  "Zeri",
  "Yone",
  "Mel",
]);

const IN_PROGRESS_IDS = new Set<string>([]);

type FilterType = "supported" | "not-supported";

interface Champion {
  id: string;
  name: string;
}

type SubmitState = "idle" | "loading" | "success" | "error";

function champTileUrl(id: string) {
  return `https://ddragon.leagueoflegends.com/cdn/img/champion/tiles/${id}_0.jpg`;
}

function formatCount(n: number): string {
  if (n >= 1000) return "1K";
  return String(n);
}

async function fetchAllChampions(): Promise<Champion[]> {
  try {
    const versionsRes = await fetch(
      "https://ddragon.leagueoflegends.com/api/versions.json",
    );
    const versions: string[] = await versionsRes.json();
    const latestVersion = versions[0];

    const champRes = await fetch(
      `https://ddragon.leagueoflegends.com/cdn/${latestVersion}/data/en_US/champion.json`,
    );
    const champData = await champRes.json();
    const data: Record<string, { id: string; name: string }> = champData.data;
    return Object.values(data)
      .map((c) => ({ id: c.id, name: c.name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  } catch {
    return [];
  }
}

const FILTER_KEYS: { value: FilterType; key: string }[] = [
  { value: "supported", key: "lol-filter-supported" },
  { value: "not-supported", key: "lol-filter-not-supported" },
];

export default function LoLChampions() {
  const { t } = useTrans(["games"]);
  const [champions, setChampions] = useState<Champion[]>([]);
  const [filter, setFilter] = useState<FilterType>("supported");
  const [feedbackChampion, setFeedbackChampion] = useState<Champion | null>(
    null,
  );
  const [feedbackText, setFeedbackText] = useState("");
  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const [requestCounts, setRequestCounts] = useState<Record<string, number>>(
    {},
  );
  const [userVotes, setUserVotes] = useState<Set<string>>(new Set());
  const [likeCounts, setLikeCounts] = useState<Record<string, number>>({});
  const [userLikes, setUserLikes] = useState<Set<string>>(new Set());
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);

  const { user, token } = useUser();

  const authHeader = token ? `Bearer ${token.token}` : "";

  useEffect(() => {
    fetchAllChampions().then(setChampions);
  }, []);

  useEffect(() => {
    fetchRequestData();
  }, [user?.email, token?.token]);

  async function fetchRequestData() {
    try {
      const res = await fetch("/api/lol/champion-request/", {
        headers: authHeader ? { Authorization: authHeader } : {},
      });
      if (!res.ok) return;
      const data = (await res.json()) as {
        requestCounts: Record<string, number>;
        requestUserVotes: string[];
        likeCounts: Record<string, number>;
        likeUserVotes: string[];
      };
      setRequestCounts(data.requestCounts);
      setUserVotes(new Set(data.requestUserVotes));
      setLikeCounts(data.likeCounts);
      setUserLikes(new Set(data.likeUserVotes));
    } catch {
      // ignore
    }
  }

  async function handleVote(champ: Champion, type: "request" | "like") {
    if (!user?.isLoggedIn) {
      setLoginDialogOpen(true);
      return;
    }

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (authHeader) headers.Authorization = authHeader;

    // Like toggle: 이미 liked면 삭제
    if (type === "like" && userLikes.has(champ.id)) {
      const prevCount = likeCounts[champ.id] ?? 0;
      setLikeCounts((prev) => ({
        ...prev,
        [champ.id]: Math.max(0, prevCount - 1),
      }));
      setUserLikes((prev) => {
        const next = new Set(prev);
        next.delete(champ.id);
        return next;
      });

      try {
        const res = await fetch("/api/lol/champion-request/", {
          method: "DELETE",
          headers,
          body: JSON.stringify({ championId: champ.id }),
        });
        const data = (await res.json()) as { count: number };
        if (res.ok) {
          setLikeCounts((prev) => ({ ...prev, [champ.id]: data.count }));
        }
      } catch {
        setLikeCounts((prev) => ({ ...prev, [champ.id]: prevCount }));
        setUserLikes((prev) => new Set(prev).add(champ.id));
      }
      return;
    }

    // Request toggle: 이미 voted면 취소
    if (type === "request" && userVotes.has(champ.id)) {
      const prevCount = requestCounts[champ.id] ?? 0;
      setRequestCounts((prev) => ({
        ...prev,
        [champ.id]: Math.max(0, prevCount - 1),
      }));
      setUserVotes((prev) => {
        const next = new Set(prev);
        next.delete(champ.id);
        return next;
      });

      try {
        const res = await fetch("/api/lol/champion-request/", {
          method: "DELETE",
          headers,
          body: JSON.stringify({
            championId: champ.id,
            type: "request",
          }),
        });
        const data = (await res.json()) as { count: number };
        if (res.ok) {
          setRequestCounts((prev) => ({ ...prev, [champ.id]: data.count }));
        }
      } catch {
        setRequestCounts((prev) => ({ ...prev, [champ.id]: prevCount }));
        setUserVotes((prev) => new Set(prev).add(champ.id));
      }
      return;
    }

    const [counts, setCounts] =
      type === "like"
        ? [likeCounts, setLikeCounts]
        : [requestCounts, setRequestCounts];
    const setVoted = type === "like" ? setUserLikes : setUserVotes;
    const prevCount = counts[champ.id] ?? 0;

    // Optimistic update
    setCounts((prev) => ({ ...prev, [champ.id]: prevCount + 1 }));
    setVoted((prev) => new Set(prev).add(champ.id));

    try {
      const res = await fetch("/api/lol/champion-request/", {
        method: "POST",
        headers,
        body: JSON.stringify({ championId: champ.id, type }),
      });
      const data = (await res.json()) as { count: number };
      if (res.ok) {
        setCounts((prev) => ({ ...prev, [champ.id]: data.count }));
      }
    } catch {
      setCounts((prev) => ({ ...prev, [champ.id]: prevCount }));
      setVoted((prev) => {
        const next = new Set(prev);
        next.delete(champ.id);
        return next;
      });
    }
  }

  const filterCounts = {
    supported: champions.filter(
      (c) => SUPPORTED_IDS.has(c.id) || IN_PROGRESS_IDS.has(c.id),
    ).length,
    "not-supported": champions.filter(
      (c) => !SUPPORTED_IDS.has(c.id) && !IN_PROGRESS_IDS.has(c.id),
    ).length,
  };

  const filtered = champions.filter((c) => {
    if (filter === "supported")
      return SUPPORTED_IDS.has(c.id) || IN_PROGRESS_IDS.has(c.id);
    return !SUPPORTED_IDS.has(c.id) && !IN_PROGRESS_IDS.has(c.id);
  });

  const handleFeedbackSubmit = async () => {
    if (!feedbackChampion || !feedbackText.trim()) return;
    setSubmitState("loading");
    try {
      const res = await fetch("/api/lol/champion-feedback/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          championName: feedbackChampion.name,
          message: feedbackText,
          email: user?.email,
        }),
      });
      setSubmitState(res.ok ? "success" : "error");
    } catch {
      setSubmitState("error");
    }
  };

  const closeFeedback = () => {
    setFeedbackChampion(null);
    setFeedbackText("");
    setSubmitState("idle");
  };

  return (
    <>
      <div className="flex flex-col gap-6">
        {/* Title + Filters */}
        <div className="flex flex-col gap-3 smd:flex-row smd:flex-wrap smd:items-center smd:gap-3">
          <h2 className="text-2xl laptop:text-3xl">
            {t("Supported Champions")}
          </h2>

          {/* 모바일: 세그먼트 컨트롤 */}
          <div className="grid grid-cols-2 rounded-xl border border-coolgray-20 p-0.5 smd:hidden">
            {FILTER_KEYS.map((f) => (
              <button
                key={f.value}
                onClick={() => setFilter(f.value)}
                className={cn(
                  "flex items-center justify-center gap-1.5 rounded-lg py-2 text-sm font-500 transition-colors",
                  filter === f.value
                    ? "bg-coolgray-90 text-white"
                    : "text-coolgray-50",
                )}
              >
                {t(f.key)}
                {champions.length > 0 && (
                  <span
                    className={cn(
                      "rounded-full px-1.5 py-0.5 text-[10px] font-600",
                      filter === f.value
                        ? "bg-white/20 text-white"
                        : "bg-coolgray-10 text-coolgray-50",
                    )}
                  >
                    {filterCounts[f.value]}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* 데스크탑: pill 버튼 */}
          <div className="hidden smd:flex smd:flex-wrap smd:gap-1.5">
            {FILTER_KEYS.map((f) => {
              const count = filterCounts[f.value];
              return (
                <button
                  key={f.value}
                  onClick={() => setFilter(f.value)}
                  className={cn(
                    "flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-500 transition-colors",
                    filter === f.value
                      ? "border-coolgray-90 bg-coolgray-90 text-white"
                      : "border-coolgray-20 bg-white text-coolgray-60 hover:border-coolgray-50",
                  )}
                >
                  {t(f.key)}
                  {champions.length > 0 && (
                    <span
                      className={cn(
                        "flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[10px] font-600 leading-none",
                        filter === f.value
                          ? "bg-white/20 text-white"
                          : "bg-coolgray-10 text-coolgray-50",
                      )}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Champion Grid */}
        {champions.length === 0 ? (
          <div className="laptop:grid-cols-14 grid grid-cols-4 gap-2 smd:grid-cols-7 smd:gap-1.5 md:grid-cols-10">
            {Array.from({ length: 42 }).map((_, i) => (
              <div
                key={i}
                className="aspect-square w-full animate-pulse rounded-lg bg-coolgray-10"
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {filter === "not-supported" && (
              <div className="bg-coolgray-5 rounded-xl border border-coolgray-10 px-4 py-3 text-sm text-coolgray-60">
                {t("lol-not-supported-guide")}
              </div>
            )}
            <div className="laptop:grid-cols-14 grid grid-cols-4 gap-2 smd:grid-cols-7 smd:gap-1.5 md:grid-cols-10">
              {filtered.map((champ) => {
                const isSupported = SUPPORTED_IDS.has(champ.id);
                const isInProgress = IN_PROGRESS_IDS.has(champ.id);
                return (
                  <ChampionCard
                    key={champ.id}
                    champ={champ}
                    isSupported={isSupported}
                    isInProgress={isInProgress}
                    requestCount={requestCounts[champ.id] ?? 0}
                    userVoted={userVotes.has(champ.id)}
                    likeCount={likeCounts[champ.id] ?? 0}
                    userLiked={userLikes.has(champ.id)}
                    onFeedback={() => setFeedbackChampion(champ)}
                    onRequestSupport={() => handleVote(champ, "request")}
                    onLike={() => handleVote(champ, "like")}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Feedback Modal */}
      {feedbackChampion && (
        <FeedbackModal
          champion={feedbackChampion}
          feedbackText={feedbackText}
          submitState={submitState}
          onTextChange={setFeedbackText}
          onSubmit={handleFeedbackSubmit}
          onClose={closeFeedback}
        />
      )}

      <LoginDialog
        open={loginDialogOpen}
        onOpenChange={setLoginDialogOpen}
        description={<p className="text-base">{t("lol-login-to-vote")}</p>}
      />
    </>
  );
}

function ChampionCard({
  champ,
  isSupported,
  isInProgress,
  requestCount,
  userVoted,
  likeCount,
  userLiked,
  onFeedback,
  onRequestSupport,
  onLike,
}: {
  champ: Champion;
  isSupported: boolean;
  isInProgress: boolean;
  requestCount: number;
  userVoted: boolean;
  likeCount: number;
  userLiked: boolean;
  onFeedback: () => void;
  onRequestSupport: () => void;
  onLike: () => void;
}) {
  const { t } = useTrans(["games"]);
  const isNotSupported = !isSupported && !isInProgress;
  const [imgSrc, setImgSrc] = useState(champTileUrl(champ.id));
  const isMobile = useIsMobileView();
  const [showOverlay, setShowOverlay] = useState(false);

  return (
    <div
      className="group flex flex-col items-center gap-1 smd:gap-0.5"
      onClick={isMobile ? () => setShowOverlay((v) => !v) : undefined}
    >
      <div className="relative w-full">
        {/* Image — dimmed for not-supported */}
        <div
          className={cn(
            "relative aspect-square w-full overflow-hidden rounded-md",
            isNotSupported && "opacity-60",
          )}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={imgSrc}
            alt={champ.name}
            className={cn(
              "h-full w-full object-cover object-top",
              imgSrc === "/logo.png" && "bg-coolgray-20 object-contain p-2",
            )}
            loading="lazy"
            onError={() => setImgSrc("/logo.png")}
          />
          {isInProgress && (
            <div className="absolute inset-0 flex items-end justify-center bg-yellow-400/20 pb-1">
              <span className="rounded bg-yellow-400 px-1 py-0.5 text-[8px] font-600 text-black smd:text-[7px]">
                {t("lol-coming")}
              </span>
            </div>
          )}
        </div>

        {/* Supported: hover overlay with like + feedback */}
        {isSupported && (
          <div
            className={cn(
              "absolute inset-0 flex flex-col items-center justify-center gap-1.5 rounded-md bg-black/60 opacity-0 transition-opacity group-hover:opacity-100",
              showOverlay && "opacity-100",
            )}
          >
            {/* Like button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onLike();
              }}
              className={cn(
                "flex items-center gap-1 rounded px-2.5 py-1.5 text-xs font-600 leading-tight transition-colors smd:px-1.5 smd:py-0.5 smd:text-[8px]",
                userLiked
                  ? "bg-red-500 text-white hover:bg-red-400"
                  : "border border-white/80 text-white hover:bg-white/10",
              )}
            >
              <HeartIcon filled={userLiked} />
              {userLiked ? t("lol-liked") : t("lol-like")}
            </button>
            {/* Feedback button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onFeedback();
              }}
              className="rounded border border-white/60 px-2.5 py-1.5 text-xs font-500 text-white/90 transition-colors hover:border-white hover:text-white smd:px-1.5 smd:py-0.5 smd:text-[8px]"
            >
              {t("lol-feedback")}
            </button>
          </div>
        )}

        {/* Supported: like count badge — top-right, always visible when count > 0 */}
        {isSupported && likeCount > 0 && (
          <div className="pointer-events-none absolute right-0.5 top-0.5 z-10 flex items-center gap-0.5 rounded-full bg-black/80 px-1.5 py-0.5">
            <HeartIcon
              filled={userLiked}
              size={8}
              className={userLiked ? "text-red-400" : "text-white/70"}
            />
            <span className="text-[9px] font-600 leading-none text-white smd:text-[8px]">
              {formatCount(likeCount)}
            </span>
          </div>
        )}

        {/* Not-supported: request support overlay */}
        {isNotSupported && !userVoted && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRequestSupport();
            }}
            className={cn(
              "absolute inset-0 flex flex-col items-center justify-center rounded-md bg-black/0 opacity-0 transition-all group-hover:bg-black/55 group-hover:opacity-100",
              showOverlay && "bg-black/55 opacity-100",
            )}
          >
            <span className="rounded border border-white px-2.5 py-1.5 text-xs font-600 leading-tight text-white smd:px-1.5 smd:py-0.5 smd:text-[8px]">
              {t("lol-request")}
            </span>
          </button>
        )}
        {isNotSupported && userVoted && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRequestSupport();
            }}
            className="group/req absolute inset-0 flex flex-col items-center justify-center rounded-md bg-black/65 transition-colors hover:bg-black/75"
          >
            <span className="rounded bg-white px-1.5 py-0.5 text-[8px] font-600 leading-tight text-coolgray-90 group-hover/req:hidden">
              {t("lol-requested")}
            </span>
            <span className="hidden rounded bg-red-500/80 px-1.5 py-0.5 text-[8px] font-600 leading-tight text-white group-hover/req:block">
              {t("lol-cancel")}
            </span>
          </button>
        )}

        {/* Not-supported: request count badge — top-right, visible when count >= 1 */}
        {isNotSupported && requestCount > 0 && (
          <div className="pointer-events-none absolute right-0.5 top-0.5 z-10 flex items-center gap-0.5 rounded-full bg-black/80 px-1.5 py-0.5">
            <span className="text-[9px] font-600 leading-none text-white smd:text-[8px]">
              {formatCount(requestCount)}
            </span>
          </div>
        )}
      </div>

      <span className="w-full truncate text-center text-[10px] leading-tight text-coolgray-60 smd:text-[9px]">
        {champ.name}
      </span>
    </div>
  );
}

function HeartIcon({
  filled,
  size = 8,
  className,
}: {
  filled: boolean;
  size?: number;
  className?: string;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M8 13.7C7.6 13.4 1 9.2 1 5a3.5 3.5 0 0 1 7-0.4A3.5 3.5 0 0 1 15 5c0 4.2-6.6 8.4-7 8.7z" />
    </svg>
  );
}

function FeedbackModal({
  champion,
  feedbackText,
  submitState,
  onTextChange,
  onSubmit,
  onClose,
}: {
  champion: Champion;
  feedbackText: string;
  submitState: SubmitState;
  onTextChange: (v: string) => void;
  onSubmit: () => void;
  onClose: () => void;
}) {
  const { t } = useTrans(["games"]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-600">{champion.name}</h3>
            <p className="mt-1 text-sm text-coolgray-50">
              {t("lol-feedback-description")}
            </p>
          </div>
          <button
            onClick={onClose}
            className="ml-4 shrink-0 text-coolgray-40 hover:text-coolgray-70"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M15 5L5 15M5 5l10 10"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {submitState === "success" ? (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <svg
              width="40"
              height="40"
              viewBox="0 0 40 40"
              fill="none"
              className="text-green-500"
            >
              <circle
                cx="20"
                cy="20"
                r="20"
                fill="currentColor"
                fillOpacity="0.1"
              />
              <path
                d="M12 20l6 6 10-12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <p className="text-sm font-500">{t("lol-feedback-submitted")}</p>
            <button
              onClick={onClose}
              className="mt-2 rounded-lg bg-coolgray-90 px-6 py-2.5 text-sm text-white hover:bg-coolgray-70"
            >
              {t("lol-feedback-close")}
            </button>
          </div>
        ) : (
          <>
            <textarea
              ref={textareaRef}
              value={feedbackText}
              onChange={(e) => onTextChange(e.target.value)}
              placeholder={t("lol-feedback-placeholder")}
              rows={4}
              className="w-full resize-none rounded-lg border border-coolgray-20 px-4 py-3 text-sm outline-none transition-colors focus:border-coolgray-60"
              disabled={submitState === "loading"}
            />
            {submitState === "error" && (
              <p className="mt-1.5 text-xs text-red-500">
                {t("lol-feedback-failed")}
              </p>
            )}
            <div className="mt-4 flex gap-2">
              <button
                onClick={onClose}
                disabled={submitState === "loading"}
                className="flex-1 rounded-lg border border-coolgray-20 py-2.5 text-sm text-coolgray-70 hover:border-coolgray-40 disabled:opacity-40"
              >
                {t("lol-feedback-cancel")}
              </button>
              <button
                onClick={onSubmit}
                disabled={!feedbackText.trim() || submitState === "loading"}
                className="flex-1 rounded-lg bg-coolgray-90 py-2.5 text-sm text-white hover:bg-coolgray-70 disabled:opacity-40"
              >
                {submitState === "loading"
                  ? t("lol-feedback-sending")
                  : t("lol-feedback-submit")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
